import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import spformsApi from '../lib/spformsApi';
import { useTenant } from '../contexts/TenantContext';
import ShareRegistrationTools from '../components/ShareRegistrationTools';
import FormFieldsModal from '../components/IntakeGroups/FormFieldsModal';
import DiscountCodesModal from '../components/IntakeGroups/DiscountCodesModal';
import { formatDateEU } from '../utils/dateFormat';

const {
  FiPlus,
  FiRefreshCw,
  FiEdit2,
  FiX,
  FiSettings,
  FiTag,
  FiSave,
  FiImage,
  FiUploadCloud,
  FiGlobe,
  FiTrash2,
  FiCalendar,
  FiCreditCard,
} = FiIcons;

const GROUP_TYPES = [
  { value: 'camp', label: 'Camp' },
  { value: 'trial', label: 'Trial' },
  { value: 'visit', label: 'Visit' },
  { value: 'event', label: 'Event' },
  { value: 'mixed_group', label: 'Mixed Group' },
];

const GROUP_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

const DEFAULT_JERSEY_SIZES = '8Y,10Y,12Y,14Y,XS,S,M,L,XL,XXL';

function emptyForm() {
  return {
    id: null,
    title: '',
    type: 'camp',
    start_date: '',
    end_date: '',
    location: '',
    capacity: '',
    status: 'active',
    public_slug: '',
    public_enabled: true,
    price_amount: 0,
    early_bird_enabled: false,
    early_bird_until: '',
    early_bird_price: '',
    jersey_size_options: DEFAULT_JERSEY_SIZES,
    notes: '',
    banner_source_type: 'url',
    banner_image_url: '',
    banner_storage_path: '',

    wire_transfer_enabled: false,
    wire_transfer_iban: '',
    wire_transfer_beneficiary: '',
    wire_transfer_bank_name: '',
    wire_transfer_notes: '',

    wire_transfer_2_enabled: false,
    wire_transfer_2_iban: '',
    wire_transfer_2_beneficiary: '',
    wire_transfer_2_bank_name: '',
    wire_transfer_2_notes: '',
  };
}

function formatMoney(value) {
  const n = Number(value || 0);
  return `€${n.toFixed(2)}`;
}

function arrayToCsv(value) {
  if (Array.isArray(value)) return value.join(',');

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return DEFAULT_JERSEY_SIZES;

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.join(',');
    } catch {
      return trimmed;
    }
  }

  return DEFAULT_JERSEY_SIZES;
}

function getBannerPreviewUrl(group) {
  return group?.banner_image_url || '';
}

export default function IntakeGroups() {
  const { tenantId } = useTenant();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [builderGroupId, setBuilderGroupId] = useState(null);
  const [promoGroupId, setPromoGroupId] = useState(null);

  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState('');

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadGroups = async () => {
    if (!tenantId) {
      setGroups([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');

      const data = await spformsApi.intakeGroupsList(tenantId);
      setGroups(data);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load intake groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [tenantId]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;

    return groups.filter((group) => {
      return (
        String(group.title || '').toLowerCase().includes(q) ||
        String(group.public_slug || '').toLowerCase().includes(q) ||
        String(group.location || '').toLowerCase().includes(q) ||
        String(group.type || '').toLowerCase().includes(q)
      );
    });
  }, [groups, search]);

  const openCreateModal = () => {
    setEditingGroup(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEditModal = (group) => {
    setEditingGroup(group);

    setForm({
      id: group.id,
      title: group.title || '',
      type: group.type || 'camp',
      start_date: group.start_date || '',
      end_date: group.end_date || '',
      location: group.location || '',
      capacity:
        group.capacity === null || group.capacity === undefined
          ? ''
          : String(group.capacity),
      status: group.status || 'active',
      public_slug: group.public_slug || '',
      public_enabled:
        group.public_enabled === null || group.public_enabled === undefined
          ? true
          : Boolean(group.public_enabled),
      price_amount:
        group.price_amount === null || group.price_amount === undefined
          ? 0
          : group.price_amount,
      early_bird_enabled: Boolean(group.early_bird_enabled),
      early_bird_until: group.early_bird_until || '',
      early_bird_price:
        group.early_bird_price === null || group.early_bird_price === undefined
          ? ''
          : group.early_bird_price,
      jersey_size_options: arrayToCsv(group.jersey_size_options),
      notes: group.notes || '',
      banner_source_type: group.banner_source_type || 'url',
      banner_image_url: group.banner_image_url || '',
      banner_storage_path: group.banner_storage_path || '',

      wire_transfer_enabled: Boolean(group.wire_transfer_enabled),
      wire_transfer_iban: group.wire_transfer_iban || '',
      wire_transfer_beneficiary: group.wire_transfer_beneficiary || '',
      wire_transfer_bank_name: group.wire_transfer_bank_name || '',
      wire_transfer_notes: group.wire_transfer_notes || '',

      wire_transfer_2_enabled: Boolean(group.wire_transfer_2_enabled),
      wire_transfer_2_iban: group.wire_transfer_2_iban || '',
      wire_transfer_2_beneficiary: group.wire_transfer_2_beneficiary || '',
      wire_transfer_2_bank_name: group.wire_transfer_2_bank_name || '',
      wire_transfer_2_notes: group.wire_transfer_2_notes || '',
    });

    setShowModal(true);
  };

  const updateForm = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'title' && !editingGroup) {
        next.public_slug = spformsApi.slugify(value);
      }

      if (field === 'banner_source_type' && value === 'url') {
        next.banner_storage_path = '';
      }

      return next;
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGroup(null);
    setForm(emptyForm());
    setUploading(false);
  };

  const handleBannerUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!tenantId) {
      alert('Tenant not available');
      return;
    }

    if (!form.id) {
      alert('Save the event once before uploading a banner image.');
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);

      const result = await spformsApi.uploadPublicAsset({
        tenantId,
        intakeGroupId: form.id,
        file,
        folder: 'banners',
      });

      setForm((prev) => ({
        ...prev,
        banner_source_type: 'upload',
        banner_image_url: result.url,
        banner_storage_path: result.path,
      }));
    } catch (error) {
      alert(error.message || 'Banner upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removeBanner = () => {
    setForm((prev) => ({
      ...prev,
      banner_image_url: '',
      banner_storage_path: '',
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      setErrorMessage('Tenant not available');
      return;
    }

    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const payload = {
        ...form,
        tenant_id: tenantId,
      };

      if (editingGroup?.id) {
        await spformsApi.intakeGroupUpdate(payload);
        setSuccessMessage('Event updated successfully');
      } else {
        const newGroupId = await spformsApi.intakeGroupCreate(payload);
        await spformsApi.seedDefaultFields(tenantId, newGroupId);
        setSuccessMessage('Event created successfully with default form fields');
      }

      closeModal();
      await loadGroups();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const renderBankSettings = ({
    number,
    enabledField,
    ibanField,
    beneficiaryField,
    bankNameField,
    notesField,
  }) => {
    const isSecond = number === 2;

    return (
      <section className="rounded-3xl border-2 border-emerald-100 bg-emerald-50/40 p-8 space-y-6">
        <div className="flex items-center gap-4">
          <input
            id={enabledField}
            type="checkbox"
            checked={Boolean(form[enabledField])}
            onChange={(e) => updateForm(enabledField, e.target.checked)}
            className="w-6 h-6 rounded border-2 border-emerald-300 text-emerald-600"
          />

          <label
            htmlFor={enabledField}
            className="text-sm font-black text-emerald-900 uppercase tracking-widest"
          >
            Enable Bank Transfer {number} / IBAN
          </label>
        </div>

        {form[enabledField] ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label className="label-small">
                {isSecond ? 'Beneficiary 2' : 'Beneficiary'}
              </label>
              <input
                value={form[beneficiaryField]}
                onChange={(e) => updateForm(beneficiaryField, e.target.value)}
                className="field-input"
                placeholder="Elite Soccer Skills"
              />
            </div>

            <div className="space-y-2">
              <label className="label-small">
                {isSecond ? 'Bank Name 2' : 'Bank Name'}
              </label>
              <input
                value={form[bankNameField]}
                onChange={(e) => updateForm(bankNameField, e.target.value)}
                className="field-input"
                placeholder="Bank name"
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <label className="label-small">
                {isSecond ? 'IBAN 2' : 'IBAN'}
              </label>
              <input
                value={form[ibanField]}
                onChange={(e) => updateForm(ibanField, e.target.value)}
                className="field-input font-mono"
                placeholder="GR00 0000 0000 0000 0000 0000 000"
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <label className="label-small">
                {isSecond ? 'Wire Transfer Notes 2' : 'Wire Transfer Notes'}
              </label>
              <textarea
                rows={3}
                value={form[notesField]}
                onChange={(e) => updateForm(notesField, e.target.value)}
                className="field-input"
                placeholder="Please include participant name in the payment reference."
              />
            </div>
          </div>
        ) : null}
      </section>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter">
            Intake Groups
          </h1>
          <p className="text-gray-500 font-medium tracking-tight">
            Configure event pricing, public forms, banners, discount codes, and bank transfer details.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={loadGroups}
            className="w-12 h-12 bg-white border rounded-2xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
          >
            <SafeIcon icon={FiRefreshCw} />
          </button>

          <button
            type="button"
            onClick={openCreateModal}
            className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all flex items-center gap-3"
          >
            <SafeIcon icon={FiPlus} />
            Create Event
          </button>
        </div>
      </div>

      {errorMessage ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
          {errorMessage}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-700">
          {successMessage}
        </div>
      ) : null}

      <div className="bg-white border rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="p-8 border-b flex items-center justify-between bg-gray-50/50">
          <input
            placeholder="Search events..."
            className="px-5 py-3 bg-white border border-gray-200 rounded-2xl text-sm font-bold w-72 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="text-xs font-black uppercase tracking-widest text-gray-400">
            {filteredGroups.length} event{filteredGroups.length === 1 ? '' : 's'}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-white text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 border-b">
              <tr>
                <th className="px-8 py-5">Event Details</th>
                <th className="px-8 py-5">Pricing</th>
                <th className="px-8 py-5">Registration</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-8 py-20 text-center text-xs font-black uppercase tracking-[0.3em] text-gray-400 animate-pulse"
                  >
                    Loading events...
                  </td>
                </tr>
              ) : filteredGroups.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    className="px-8 py-20 text-center text-sm font-bold text-gray-400"
                  >
                    No events found.
                  </td>
                </tr>
              ) : (
                filteredGroups.map((group) => {
                  const bannerUrl = getBannerPreviewUrl(group);
                  const bankCount =
                    Number(Boolean(group.wire_transfer_enabled)) +
                    Number(Boolean(group.wire_transfer_2_enabled));

                  return (
                    <tr
                      key={group.id}
                      className="hover:bg-gray-50/50 transition-all group"
                    >
                      <td className="px-8 py-8">
                        <div className="flex items-start gap-4">
                          {bannerUrl ? (
                            <img
                              src={bannerUrl}
                              className="w-14 h-14 rounded-2xl object-cover bg-gray-100 border"
                              alt=""
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-2xl bg-gray-100 border flex items-center justify-center text-gray-300">
                              <SafeIcon icon={FiImage} />
                            </div>
                          )}

                          <div>
                            <div className="font-black text-gray-900 text-lg tracking-tight leading-none">
                              {group.title}
                            </div>

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-[10px] font-black px-2 py-1 bg-indigo-50 text-indigo-600 rounded-md uppercase tracking-widest leading-none">
                                {group.type}
                              </span>

                              {group.start_date ? (
                                <span className="flex items-center gap-1 text-[10px] font-black px-2 py-1 bg-emerald-50 text-emerald-600 rounded-md uppercase tracking-widest leading-none">
                                  <SafeIcon icon={FiCalendar} size={10} />
                                  {formatDateEU(group.start_date)}
                                </span>
                              ) : null}

                              {bankCount > 0 ? (
                                <span className="flex items-center gap-1 text-[10px] font-black px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md uppercase tracking-widest leading-none">
                                  <SafeIcon icon={FiCreditCard} size={10} />
                                  {bankCount} IBAN
                                </span>
                              ) : null}
                            </div>

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-[10px] font-black px-2 py-1 bg-gray-100 text-gray-500 rounded-md uppercase tracking-widest">
                                {group.location || 'No location'}
                              </span>

                              <span className="text-[10px] font-black px-2 py-1 bg-gray-100 text-gray-500 rounded-md uppercase tracking-widest">
                                {group.status || 'active'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-8 py-8">
                        <div className="text-xl font-black text-gray-900">
                          {formatMoney(group.price_amount)}
                        </div>

                        {group.early_bird_enabled ? (
                          <div className="mt-1 text-[10px] text-emerald-600 font-black uppercase tracking-tighter">
                            Early Bird: {formatMoney(group.early_bird_price)} until{' '}
                            {formatDateEU(group.early_bird_until)}
                          </div>
                        ) : (
                          <div className="mt-1 text-[10px] text-gray-400 font-black uppercase tracking-tighter">
                            No early bird
                          </div>
                        )}
                      </td>

                      <td className="px-8 py-8">
                        <ShareRegistrationTools
                          publicSlug={group.public_slug}
                          title={group.title}
                          compact
                        />
                      </td>

                      <td className="px-8 py-8 text-right">
                        <div className="flex justify-end gap-3 opacity-100 md:opacity-0 group-hover:opacity-100 transition-all">
                          <button
                            type="button"
                            onClick={() => setPromoGroupId(group.id)}
                            className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl hover:bg-emerald-100 transition-all"
                            title="Discount Codes"
                          >
                            <SafeIcon icon={FiTag} size={20} />
                          </button>

                          <button
                            type="button"
                            onClick={() => setBuilderGroupId(group.id)}
                            className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl hover:bg-indigo-100 transition-all"
                            title="Form Builder"
                          >
                            <SafeIcon icon={FiSettings} size={20} />
                          </button>

                          <button
                            type="button"
                            onClick={() => openEditModal(group)}
                            className="p-3 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 transition-all"
                            title="Edit Event"
                          >
                            <SafeIcon icon={FiEdit2} size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
          <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-4xl overflow-hidden">
            <div className="p-8 border-b flex items-center justify-between bg-gray-50/50">
              <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                {editingGroup ? 'Edit Event' : 'New Event'}
              </h2>

              <button
                type="button"
                onClick={closeModal}
                className="w-10 h-10 flex items-center justify-center bg-gray-200/50 rounded-full"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>

            <form
              onSubmit={handleSave}
              className="p-10 space-y-8 max-h-[75vh] overflow-y-auto"
            >
              <section className="rounded-3xl border-2 border-gray-100 bg-gray-50/50 p-8 space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                    <SafeIcon icon={FiImage} />
                  </div>

                  <div>
                    <h3 className="text-sm font-black uppercase tracking-widest text-gray-700">
                      Event Banner
                    </h3>
                    <p className="text-xs font-medium text-gray-400">
                      This image appears below the event title on the public form.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="label-small">Source Type</label>
                    <select
                      value={form.banner_source_type}
                      onChange={(e) => updateForm('banner_source_type', e.target.value)}
                      className="field-input"
                    >
                      <option value="url">Image URL</option>
                      <option value="upload">Upload Image</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="label-small">
                      {form.banner_source_type === 'url'
                        ? 'Banner Image URL'
                        : 'Upload Banner Image'}
                    </label>

                    {form.banner_source_type === 'url' ? (
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          <SafeIcon icon={FiGlobe} />
                        </div>

                        <input
                          type="url"
                          value={form.banner_image_url}
                          onChange={(e) => updateForm('banner_image_url', e.target.value)}
                          placeholder="https://example.com/banner.jpg"
                          className="field-input pl-12"
                        />
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleBannerUpload}
                          disabled={uploading || !form.id}
                          className="hidden"
                          id="banner-upload"
                        />

                        <label
                          htmlFor="banner-upload"
                          className={`flex items-center justify-center gap-3 w-full px-6 py-4 rounded-xl border-2 border-dashed font-bold transition-all ${
                            form.id
                              ? 'border-gray-200 bg-white text-gray-500 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer'
                              : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          <SafeIcon
                            icon={uploading ? FiRefreshCw : FiUploadCloud}
                            className={uploading ? 'animate-spin' : ''}
                          />

                          {uploading
                            ? 'Uploading...'
                            : form.id
                            ? form.banner_image_url
                              ? 'Change Image'
                              : 'Select Image'
                            : 'Save event first to upload'}
                        </label>

                        {!form.id ? (
                          <p className="mt-2 text-xs font-medium text-amber-600">
                            Upload is available after the event is saved once.
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                {form.banner_image_url ? (
                  <div className="relative rounded-3xl overflow-hidden border-4 border-white shadow-xl h-56 group bg-gray-100">
                    <img
                      src={form.banner_image_url}
                      className="w-full h-full object-cover"
                      alt="Banner Preview"
                    />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        type="button"
                        onClick={removeBanner}
                        className="bg-red-500 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-2"
                      >
                        <SafeIcon icon={FiTrash2} /> Remove Banner
                      </button>
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="md:col-span-2 space-y-2">
                  <label className="label-small">Event Title</label>
                  <input
                    required
                    value={form.title}
                    onChange={(e) => updateForm('title', e.target.value)}
                    className="field-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="label-small">Event Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => updateForm('type', e.target.value)}
                    className="field-input"
                  >
                    {GROUP_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="label-small">Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => updateForm('status', e.target.value)}
                    className="field-input"
                  >
                    {GROUP_STATUSES.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="label-small">Start Date</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => updateForm('start_date', e.target.value)}
                    className="field-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="label-small">End Date</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => updateForm('end_date', e.target.value)}
                    className="field-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="label-small">Location</label>
                  <input
                    value={form.location}
                    onChange={(e) => updateForm('location', e.target.value)}
                    className="field-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="label-small">Capacity</label>
                  <input
                    type="number"
                    min="0"
                    value={form.capacity}
                    onChange={(e) => updateForm('capacity', e.target.value)}
                    className="field-input"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className="label-small">Public Slug</label>
                  <input
                    value={form.public_slug}
                    onChange={(e) => updateForm('public_slug', e.target.value)}
                    className="field-input font-mono"
                  />
                </div>
              </section>

              <section className="rounded-3xl border-2 border-indigo-100 bg-indigo-50/40 p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="label-small">Base Price (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={form.price_amount}
                      onChange={(e) => updateForm('price_amount', e.target.value)}
                      className="field-input"
                    />
                  </div>

                  <div className="flex items-center gap-4 pt-7">
                    <input
                      id="early_bird_enabled"
                      type="checkbox"
                      checked={form.early_bird_enabled}
                      onChange={(e) => updateForm('early_bird_enabled', e.target.checked)}
                      className="w-6 h-6 rounded border-2 border-indigo-300 text-indigo-600"
                    />

                    <label
                      htmlFor="early_bird_enabled"
                      className="text-sm font-black text-indigo-900 uppercase tracking-widest"
                    >
                      Enable Early Bird
                    </label>
                  </div>
                </div>

                {form.early_bird_enabled ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="label-small">Early Bird Price (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={form.early_bird_price}
                        onChange={(e) => updateForm('early_bird_price', e.target.value)}
                        className="field-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="label-small">Early Bird Until</label>
                      <input
                        type="date"
                        value={form.early_bird_until}
                        onChange={(e) => updateForm('early_bird_until', e.target.value)}
                        className="field-input"
                      />
                    </div>
                  </div>
                ) : null}
              </section>

              {renderBankSettings({
                number: 1,
                enabledField: 'wire_transfer_enabled',
                ibanField: 'wire_transfer_iban',
                beneficiaryField: 'wire_transfer_beneficiary',
                bankNameField: 'wire_transfer_bank_name',
                notesField: 'wire_transfer_notes',
              })}

              {renderBankSettings({
                number: 2,
                enabledField: 'wire_transfer_2_enabled',
                ibanField: 'wire_transfer_2_iban',
                beneficiaryField: 'wire_transfer_2_beneficiary',
                bankNameField: 'wire_transfer_2_bank_name',
                notesField: 'wire_transfer_2_notes',
              })}

              <section className="space-y-2">
                <label className="label-small">Jersey Size Options</label>
                <input
                  value={form.jersey_size_options}
                  onChange={(e) => updateForm('jersey_size_options', e.target.value)}
                  className="field-input"
                  placeholder="8Y,10Y,12Y,S,M,L,XL"
                />

                <p className="text-xs font-medium text-gray-400">
                  Comma separated values. These can be used by the default jersey size field in the form builder.
                </p>
              </section>

              <section className="flex items-center gap-4 rounded-3xl border-2 border-gray-100 bg-gray-50 p-6">
                <input
                  id="public_enabled"
                  type="checkbox"
                  checked={form.public_enabled}
                  onChange={(e) => updateForm('public_enabled', e.target.checked)}
                  className="w-6 h-6 rounded border-2 border-gray-300 text-indigo-600"
                />

                <label
                  htmlFor="public_enabled"
                  className="text-sm font-black text-gray-700 uppercase tracking-widest"
                >
                  Public registration enabled
                </label>
              </section>

              <section className="space-y-2">
                <label className="label-small">Notes / Public Description</label>
                <textarea
                  rows={4}
                  value={form.notes}
                  onChange={(e) => updateForm('notes', e.target.value)}
                  className="field-input"
                />
              </section>

              <div className="flex justify-end gap-4 pt-8 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-8 py-4 text-xs font-black text-gray-400 uppercase tracking-widest"
                >
                  Close
                </button>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-12 py-4 bg-indigo-600 text-white rounded-[1.5rem] text-xs font-black uppercase tracking-[0.2em] shadow-2xl shadow-indigo-200 hover:bg-indigo-700 transition-all disabled:opacity-60 flex items-center gap-3"
                >
                  <SafeIcon icon={FiSave} />
                  {saving ? 'Saving...' : 'Save Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {builderGroupId ? (
        <FormFieldsModal
          groupId={builderGroupId}
          tenantId={tenantId}
          onClose={() => {
            setBuilderGroupId(null);
            loadGroups();
          }}
        />
      ) : null}

      {promoGroupId ? (
        <DiscountCodesModal
          groupId={promoGroupId}
          tenantId={tenantId}
          onClose={() => {
            setPromoGroupId(null);
            loadGroups();
          }}
        />
      ) : null}

      <style>{`
        .label-small { font-size: 10px; font-weight: 900; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.2em; }
        .field-input { width: 100%; padding: 1rem 1.5rem; border-radius: 1rem; border: 2px solid #f3f4f6; font-weight: 800; color: #111827; background: white; outline: none; transition: all 0.2s ease; }
        .field-input:focus { border-color: #4f46e5; }
      `}</style>
    </div>
  );
}